import { Check, Loader2 } from "lucide-react";
import { useState } from "react";
import { NounSvg } from "@/components/noun-svg";
import { Button } from "@/components/ui/button";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { useAuth } from "@/contexts/auth-context";
import { useNounSvg } from "@/hooks/use-noun-svg";
import { useUpdateAvatar } from "@/hooks/use-users";
import { AVATAR_PRESETS, type AvatarPreset, presetId, presetLabel } from "@/lib/avatar-presets";
import type { User } from "@/lib/types";
import { cn } from "@/lib/utils";

export function AvatarPickerDialog({
  user,
  open,
  onOpenChange,
}: {
  user: User;
  open: boolean;
  onOpenChange: (open: boolean) => void;
}) {
  const { user: currentUser, setUser } = useAuth();
  const updateAvatar = useUpdateAvatar();
  const [error, setError] = useState<string | null>(null);
  const isCurrentUser = currentUser?.id === user.id;

  const handlePick = async (avatarUrl: string | null) => {
    setError(null);
    try {
      const updated = await updateAvatar.mutateAsync({ userId: user.id, avatarUrl });
      if (isCurrentUser) {
        setUser(updated);
      }
      onOpenChange(false);
    } catch (caught) {
      setError(caught instanceof Error ? caught.message : "Failed to update avatar");
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-lg">
        <DialogHeader>
          <DialogTitle>Choose an avatar</DialogTitle>
          <DialogDescription>
            Pick a fun avatar for {isCurrentUser ? "yourself" : user.name}. You can change or reset
            it any time.
          </DialogDescription>
        </DialogHeader>

        <div className="grid grid-cols-6 gap-3 py-2">
          {AVATAR_PRESETS.map((preset) => {
            const id = presetId(preset);
            return (
              <PresetTile
                key={id}
                preset={preset}
                selected={user.avatar_url === id}
                disabled={updateAvatar.isPending}
                onPick={() => handlePick(id)}
              />
            );
          })}
        </div>

        {error && (
          <p className="text-[12px] text-destructive" role="alert">
            {error}
          </p>
        )}

        <DialogFooter className="flex items-center justify-between gap-2 sm:justify-between">
          <Button
            type="button"
            variant="ghost"
            onClick={() => handlePick(null)}
            disabled={updateAvatar.isPending || !user.avatar_url}
          >
            Reset to default
          </Button>
          <div className="flex items-center gap-2">
            {updateAvatar.isPending && (
              <Loader2 className="h-4 w-4 animate-spin text-muted-foreground" />
            )}
            <Button
              type="button"
              variant="outline"
              onClick={() => onOpenChange(false)}
              disabled={updateAvatar.isPending}
            >
              Close
            </Button>
          </div>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

function PresetTile({
  preset,
  selected,
  disabled,
  onPick,
}: {
  preset: AvatarPreset;
  selected: boolean;
  disabled: boolean;
  onPick: () => void;
}) {
  const { data: svg } = useNounSvg(preset.nounId);
  const label = presetLabel(preset);
  return (
    <button
      type="button"
      title={label}
      aria-label={label}
      onClick={onPick}
      disabled={disabled}
      className={cn(
        "relative flex aspect-square items-center justify-center overflow-hidden rounded-full transition-all",
        preset.bgClass,
        "hover:ring-2 hover:ring-primary/40 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-primary",
        selected && "ring-2 ring-primary",
        disabled && "opacity-60",
      )}
    >
      {svg && <NounSvg svg={svg} />}
      {selected && (
        <span className="absolute inset-0 flex items-center justify-center rounded-full bg-black/30">
          <Check className="h-5 w-5 text-white drop-shadow" />
        </span>
      )}
    </button>
  );
}
